import React from 'react';
import styled from 'styled-components';
import { ButtonGroup, Button } from '@superops/ui-design-system';
import { Check, Times } from '@styled-icons/fa-solid';
import { Icon, ApiProgressLoader } from '@nile/components';
import { SVGIcon } from '@superops/super-icons';

const ReadViewWrapper = styled.div`
  line-height: 1;
  max-width: 100%;
`;

const EditButton = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  display: inline-block;
  line-height: 1;
  margin: 0;
  padding: 0;
  outline: 0;

  &:focus + div {
    border: 2px solid blue;
    background: #ececec;
  }
`;

const ReadViewContentWrapper = styled.div`
  box-sizing: border-box;
  // border: 2px solid transparent;
  border-radius: 5px;
  display: inline-block;
  max-width: 100%;
  transition: background 0.2s;
  width: 100%;
  &:hover {
    background: ${(props) => (props.editable ? '#ececec' : 'transparent')};
    cursor: ${(props) => (props.editable ? 'pointer' : 'default')};
  }
`;

const ContentWrapper = styled.div`
  max-width: 100%;
  position: relative;
  .Polaris-ButtonGroup {
    position: absolute;
    margin-top: 8px;
    z-index: 1;
    height: 25px;
    right: 0;
  }
  .Polaris-ButtonGroup .Polaris-Button {
    min-height: 2.6rem;
    min-width: 2.6rem;
    height: 25px;
    padding: 0 !important;
  }
`;

const HiddenButton = styled.button`
  display: none;
`;

const formRef = React.createRef();

class InlineEditField extends React.Component {
  static editStateRef = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      fieldState: null,
      apiProgress: false,
      apiResult: null
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside.bind(this));
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isEditing && !this.props.isEditing) {
      if (this.editButtonRef) {
        this.editButtonRef.focus();
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener(
      'mousedown',
      this.handleClickOutside.bind(this)
    );
  }

  handleClickOutside(event) {
    if (this.editStateRef && !this.editStateRef.contains(event.target)) {
      if (
        (event.target.classList &&
          event.target.classList.contains('value_selector__option')) ||
        event.target.classList.contains('react-select-label')
      ) {
        return;
      }
      //   this.props.onCancel();
      // Defer submit to allow focused input to blur first.
      // CurrencyField emit their final value in onBlur;
      setTimeout(() => this.onSubmit(), 0);
    }
  }

  onCancelClick = (event) => {
    event.preventDefault();
    this.props.onCancel();
  };

  onReadViewClick = (event) => {
    const { editable } = this.props;
    event.preventDefault();
    if (editable) this.props.onEditRequested();
  };

  onWrapperBlur = () => {
    console.log('on blur');
  };

  renderReadView = () => {
    const {
      editButtonLabel,
      readView,
      readViewFitContainerWidth,
      editable
    } = this.props;
    return (
      <ReadViewWrapper className="inline-edit--readview-wrapper">
        <EditButton
          aria-label={editButtonLabel}
          type="button"
          onClick={this.props.onEditRequested}
          innerRef={(ref) => {
            this.editButtonRef = ref;
          }}
        />
        <ReadViewContentWrapper
          editable={editable}
          className="inline-edit--readview"
          onClick={this.onReadViewClick}
        >
          {readView()}
        </ReadViewContentWrapper>
      </ReadViewWrapper>
    );
  };

  renderActionButtons = () => {
    const { confirmButtonLabel, cancelButtonLabel } = this.props;
    return (
      <ButtonGroup>
        <Button
          onClick={this.onSubmit}
          icon={<SVGIcon name="Check" size="sm" />}
        />

        <Button
          onClick={this.onCancelClick}
          icon={<SVGIcon name="Close" size="sm" />}
        />
      </ButtonGroup>
    );
  };

  onSubmit = (e) => {
    e?.preventDefault();
    if (this.props.showProgress) this.setState({ apiProgress: true });
    this.props.onConfirm(this.state.fieldState, this.setApiComplete);
  };

  setApiComplete = (state) => {
    if (this.props.showProgress) {
      this.setState({
        apiProgress: false,
        apiResult: state,
        fieldState: null // reset field state after each update. Else stale data will be sent in next callback
      });
    }
    setTimeout(() => {
      this.setState({
        apiResult: null
      });
    }, 2000);
  };

  updateFieldState = (value) => {
    this.setState({ fieldState: value }, () => {
      if (this.props.autoSave) {
        this.onSubmit();
      }
    });
  };

  render() {
    const {
      defaultValue,
      hideActionButtons,
      isEditing,
      isRequired,
      label,
      validate,
      saveOnEnter
    } = this.props;
    const { apiProgress, apiResult } = this.state;
    return (
      <form
        onKeyDown={(e) => {
          // onKeyDown(e);
          if (saveOnEnter && e.key === 'Enter') {
            this.onSubmit();
          }
          if (e.key === 'Esc' || e.key === 'Escape') {
            e.preventDefault();
            this.props.onCancel();
          }
        }}
        onSubmit={this.onSubmit}
        ref={formRef}
        onBlur={() => {
          console.log('on blur');
        }}
        className="inline-edit--form"
      >
        {isEditing ? (
          <ContentWrapper
            className="inline-edit inline-edit--editview__wrapper"
            // onBlur={() => this.onWrapperBlur()}
            onFocus={this.onWrapperFocus}
            ref={(ref) => {
              this.editStateRef = ref;
            }}
          >
            {this.props.editView({ updateFieldState: this.updateFieldState })}
            {!hideActionButtons && !this.props.autoSave ? (
              this.renderActionButtons()
            ) : (
              /** This is to allow Ctrl + Enter to submit without action buttons */
              <HiddenButton type="submit" />
            )}
          </ContentWrapper>
        ) : (
          <div className="flex actr inline-edit inline-edit--readview__wrapper mr-5">
            {this.renderReadView()}
            {apiProgress && <ApiProgressLoader isLoading />}
            {apiResult && (
              <ApiProgressLoader isSuccess={apiResult === 'success'} />
            )}
          </div>
        )}
      </form>
    );
  }
}

InlineEditField.defaultProps = {
  keepEditViewOpenOnBlur: false,
  hideActionButtons: false,
  isRequired: false,
  readViewFitContainerWidth: false,
  editButtonLabel: 'Edit',
  confirmButtonLabel: 'Confirm',
  cancelButtonLabel: 'Cancel'
};

export default InlineEditField;
